import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';

import {ItemsService} from './items.service';
import {validateAllFormFields} from '../../../../shared/components/validator/validator.service';
import {GeosService} from '../../../../shared/service/geos.service';
import {ToastService, Severity} from '../../../../shared/components/toast/toast.service';

import {AppSubject} from '../../../../shared/app-subject';
import {Subscription} from 'rxjs';

import {IamComponent} from '../../../abstract/IamComponent';

@Component({
  selector: 'app-organization-info',
  templateUrl: './items.component.html'
})
export class ItemsComponent extends IamComponent implements OnInit, OnDestroy {

  submitted: boolean = false;
  form = this.fb.group({
    partyTaxId: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9_.@-]*$/)]],
    partyName: ['', Validators.required],
    webAddress: [''],
    emailAddress: [''],
    phoneNumber: [''],
    faxNumber: [''],
    address1: ['', Validators.required],
    stateProvinceGeoId: ['', Validators.required],
    wardGeoId: [''],
    taxAuthorityId: ['', Validators.required],
    taxAuthorityName: [''],
    bankName: [''],
    bankAccount: [''],
    bankBranchName: [''],
    bankPartyId: [''],

    representativeName: [''],
    representativeJobTitle: [''],
    repIdentityCard: [''],
    repIdCardIssuedDate: [''],
    repIdCardIssuedBy: [''],
    representativePhoneNumber: [''],
    representativeEmail: [''],

    customerStatusId: [''],
    customerStatusCode: [''],
    establishedDate: [''],
    decreeAndCircularUsing: [{value: '', disabled: true}],
    businessType: [''],
    isMultiFacility: [false],
    facilityPseudoId: ['', Validators.maxLength(5)],
    facilityName: ['', Validators.maxLength(400)],
    facilityAddress: ['', Validators.maxLength(400)],
    facilityList: [[]]
  });
  provinces: Array<any> = new Array<any>();
  banks: Array<any> = new Array<any>();
  businessTypes: Array<any> = new Array<any>();
  displayFacilityDialog = false;
  dialogFacilityList: Array<any> = new Array<any>();
  facilityTermsAccepted = false;

  updating?: boolean;
  originalFormValues?: any;

  decreeCircular?: string;
  decreeCircularSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private itemsService: ItemsService,
    private geosService: GeosService,
    private toastService: ToastService
  ) {
    super();
  }

  ngOnInit(): void {
    this.decreeCircularSubscription = AppSubject.getDecreeCircular().subscribe((decreeCircular) => {
      if (decreeCircular) {
        this.decreeCircular = decreeCircular;
      }
    });

    const init = [];
    init.push(new Promise<void>((resolve) => {
      this.geosService.getProvinces().subscribe(({listData}) => {
        listData.forEach((item: any) => {
          this.provinces.push({value: item.toGeoId, label: item.geoName});
        });
        resolve();
      });
    }));
    init.push(new Promise<void>((resolve) => {
      this.itemsService.getBanks().subscribe({
        next: (response: any) => {
            const listData = response?.listData;
            if (listData && Array.isArray(listData)) {
              listData.forEach((item: any) => {
                if (item.partyName) {
                  this.banks.push({
                    value: item.partyName,
                    label: item.bankFullName || item.partyName,
                    partyId: item.partyId
                  });
                }
              });
            }
          resolve();
        }
      });
    }));
    init.push(new Promise<void>((resolve) => {
      this.itemsService.getBusinessTypes().subscribe(({listData}) => {
        (listData || []).forEach((item: any) => {
          this.businessTypes.push({value: item.partyClassificationId, label: item.description});
        });
        resolve();
      }, () => resolve());
    }));
    Promise.all(init).then(() => {
      this.load();
    });
  }

  ngOnDestroy(): void {
    if (this.decreeCircularSubscription) {
      this.decreeCircularSubscription.unsubscribe();
    }
  }

  load(): void {
    this.itemsService.get().subscribe((response) => {
      this.form.reset();

      const facilityList = response.facilityList || [];
      this.form.patchValue(response);
      this.form.patchValue({
        businessType: response.businessType || '',
        isMultiFacility: response.isMultiFacility === 'Y',
        facilityList
      });
      this.facilityTermsAccepted = response.businessTermsAccepted === 'Y';
      if (facilityList.length) {
        const primaryFacility = this.getPrimaryFacility(facilityList);
        this.form.patchValue({
          facilityPseudoId: primaryFacility?.pseudoId || '',
          facilityName: primaryFacility?.facilityName || '',
          facilityAddress: primaryFacility?.address1 || ''
        });
      }
      this.form.disable();
    });
  }

  update(): void {
    this.updating = true;
    this.originalFormValues = this.form.getRawValue();
    this.form.enable();
  }

  cancelUpdate(): void {
    this.updating = false;

    if (this.originalFormValues) {
      this.form.patchValue(this.originalFormValues);
    }
    this.form.disable();
  }

  onBankChange(event: any): void {
    const selectedBankName = event.value;
    if (selectedBankName) {
      const selectedBank = this.banks.find(bank => bank.value === selectedBankName);
      if (selectedBank && selectedBank.partyId) {
        this.form.patchValue({bankPartyId: selectedBank.partyId});
      }
    } else {
      this.form.patchValue({bankName: '', bankPartyId: ''});
    }
  }

  onBankClear(): void {
    this.form.get('bankName')?.setValue(null);
    this.form.get('bankPartyId')?.setValue('');
  }

  isHouseholdBusiness(): boolean {
    return this.form.get('businessType')?.value === 'PcHouseholdBusiness';
  }

  onBusinessTypeChange(): void {
    if (!this.isHouseholdBusiness()) {
      this.form.patchValue({
        isMultiFacility: false,
        facilityPseudoId: '',
        facilityName: '',
        facilityAddress: '',
        facilityList: []
      });
    }
  }

  onMultiFacilityChange(): void {
    if (!this.form.get('isMultiFacility')?.value) {
      const primaryFacility = this.getPrimaryFacility(this.form.get('facilityList')?.value || []);
      if (primaryFacility) {
        this.form.patchValue({
          facilityPseudoId: primaryFacility.pseudoId,
          facilityName: primaryFacility.facilityName,
          facilityAddress: primaryFacility.address1
        });
      }
    }
  }

  openFacilityDialog(): void {
    const facilityList = this.form.get('facilityList')?.value || [];
    this.dialogFacilityList = facilityList.length ? facilityList.map((item: any) => Object.assign({}, item)) : [this.getSingleFacility()];
    this.ensureOnePrimaryFacility(this.dialogFacilityList);
    this.displayFacilityDialog = true;
  }

  addFacilityRow(): void {
    this.dialogFacilityList.push({pseudoId: '', facilityName: '', address1: '', isPrimary: this.dialogFacilityList.length ? 'N' : 'Y'});
  }

  deleteFacilityRow(index: number): void {
    const deletedPrimary = this.isPrimaryFacility(this.dialogFacilityList[index]);
    this.dialogFacilityList.splice(index, 1);
    if (deletedPrimary) {
      this.ensureOnePrimaryFacility(this.dialogFacilityList);
    }
  }

  setPrimaryFacility(index: number, event: any): void {
    this.dialogFacilityList[index].isPrimary = event.checked ? 'Y' : 'N';
    this.dialogFacilityList[index].statusId = event.checked ? 'FcBlPrimary' : null;
  }

  saveFacilityDialog(): void {
    const facilityList = this.dialogFacilityList.filter(item => item && (item.facilityId || item.pseudoId || item.facilityName || item.address1));
    if (facilityList.length === 1) {
      facilityList[0].isPrimary = 'Y';
    }
    if (!this.validateFacilityList(facilityList, true)) {
      return;
    }
    this.form.get('facilityList')?.setValue(facilityList);
    this.displayFacilityDialog = false;
  }

  private getSingleFacility(): any {
    const currentFacility = this.getPrimaryFacility(this.form.get('facilityList')?.value || []);
    return {
      facilityId: currentFacility?.facilityId,
      pseudoId: this.form.get('facilityPseudoId')?.value,
      facilityName: this.form.get('facilityName')?.value,
      address1: this.form.get('facilityAddress')?.value,
      isPrimary: 'Y'
    };
  }

  private getPrimaryFacility(facilityList: Array<any>): any {
    if (!facilityList || !facilityList.length) {
      return null;
    }
    return facilityList.find(item => this.isPrimaryFacility(item)) || facilityList[0];
  }

  isPrimaryFacility(item: any): boolean {
    if (item?.isPrimary !== undefined && item?.isPrimary !== null) {
      return item.isPrimary === 'Y' || item.isPrimary === true;
    }
    return item?.statusId === 'FcBlPrimary';
  }

  private ensureOnePrimaryFacility(facilityList: Array<any>): void {
    if (!facilityList || !facilityList.length) {
      return;
    }
    const primaryIndex = facilityList.findIndex(item => this.isPrimaryFacility(item));
    facilityList.forEach((item: any, index: number) => {
      item.isPrimary = index === (primaryIndex >= 0 ? primaryIndex : 0) ? 'Y' : 'N';
      item.statusId = item.isPrimary === 'Y' ? 'FcBlPrimary' : null;
    });
  }

  private validateFacilityList(facilityList: Array<any>, multiFacility: boolean): boolean {
    if (!facilityList.length || facilityList.some((item: any) => !item.pseudoId || !item.facilityName || !item.address1)) {
      this.toastService.setMessage({severity: Severity.ERROR, detail: 'Vui lòng nhập đầy đủ mã, tên và địa chỉ địa điểm kinh doanh'});
      return false;
    }
    if (facilityList.some((item: any) => item.pseudoId.length > 5 || item.facilityName.length > 400 || item.address1.length > 400)) {
      this.toastService.setMessage({severity: Severity.ERROR, detail: 'Mã địa điểm tối đa 5 ký tự, tên và địa chỉ tối đa 400 ký tự'});
      return false;
    }
    if (multiFacility && facilityList.filter(item => this.isPrimaryFacility(item)).length !== 1) {
      this.toastService.setMessage({severity: Severity.ERROR, detail: 'Vui lòng chọn đúng một địa điểm kinh doanh chính'});
      return false;
    }
    return true;
  }

  onSubmit(): void {
    if (!this.submitted && validateAllFormFields(this.form)) {
      const bankAccount = this.form.get('bankAccount')?.value?.trim();
      const bankName = this.form.get('bankName')?.value;
      
      if ((bankAccount && !bankName) || (!bankAccount && bankName)) {
        this.toastService.setMessage({severity: Severity.ERROR, detail: 'Vui lòng nhập đầy đủ số tài khoản và ngân hàng hoặc để trống cả hai'});
        return;
      }

      this.submitted = true;
      const data = this.form.getRawValue();
      if (data.businessType === 'PcHouseholdBusiness') {
        const isMultiFacility = data.isMultiFacility;
        data.isMultiFacility = isMultiFacility ? 'Y' : 'N';
        data.businessTermsAccepted = this.facilityTermsAccepted ? 'Y' : 'N';
        data.facilityList = isMultiFacility ? (data.facilityList || []) : [this.getSingleFacility()];
        data.facilityList = data.facilityList.filter((item: any) => item && (item.pseudoId || item.facilityName || item.address1));
        if (data.facilityList.length === 1) {
          data.facilityList[0].isPrimary = 'Y';
        }
        if (!this.validateFacilityList(data.facilityList, isMultiFacility)) {
          this.submitted = false;
          return;
        }
      } else {
        data.isMultiFacility = 'N';
        data.facilityList = [];
      }

      this.itemsService.set(data).subscribe((response) => {
        this.submitted = false;
        if (response && response.customerPartyId) {
          this.toastService.success();
          this.updating = false;
          this.load();
        } else {
          this.toastService.error();
        }
      }, () => {
        this.submitted = false;
      });
    }
  }

}
